import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import Column from 'components/Column'
import Row from 'components/Row'
import ConfirmationTransactionStep, {
  ConfirmationTransactionStepProps,
} from 'components/StepLine/common/ConfirmationTransactionStep'
import { Check } from 'react-feather'
import styled, { useTheme } from 'styled-components'

const StyledListItem = styled(Row)`
  padding: 12px 0;
  gap: 12px;
  align-items: center;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};
  }
`

const CheckIcon = styled(Check)`
  color: ${({ theme }) => theme.accentSuccess};
  background: ${({ theme }) => `${theme.accentSuccess}1A`}; // 10% opacity
  padding: 4px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
`

const Description = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.textSecondary};
  margin: 0 0 24px 0;
  line-height: 1.5;
`

const BenefitText = styled.span`
  font-size: 16px;
  color: ${({ theme }) => theme.textPrimary};
`

type StartFarmStepProps = {
  next: () => void
  onStart: () => Promise<void>
} & Omit<ConfirmationTransactionStepProps, 'children' | 'onConfirm'>

export default function StartFarmStep({ next, onStart, ...rest }: StartFarmStepProps) {
  const theme = useTheme()

  const benefits = [
    {
      text: 'Activate reward distribution for all tokens',
      description: 'Users can start earning rewards immediately',
    },
    {
      text: 'Enable staking functionality',
      description: 'Allow users to lock their tokens for enhanced rewards',
    },
    {
      text: 'Make farm visible to all users',
      description: 'Your farm will be listed in the main dashboard',
    },
  ]

  return (
    <ConfirmationTransactionStep {...rest} onConfirm={next}>
      <Column width="100%" gap="24px">
        <Description>
          You're about to activate your farm and make it available to users. This action cannot be undone. Please review
          the following changes:
        </Description>

        <LightCard>
          {benefits.map((benefit, index) => (
            <StyledListItem key={index}>
              <CheckIcon />
              <Column gap="4px">
                <BenefitText>{benefit.text}</BenefitText>
                <span style={{ fontSize: '14px', color: theme.textSecondary }}>{benefit.description}</span>
              </Column>
            </StyledListItem>
          ))}
        </LightCard>

        <ButtonPrimary onClick={onStart} style={{ marginTop: '8px' }}>
          Start Farm
        </ButtonPrimary>
      </Column>
    </ConfirmationTransactionStep>
  )
}
